import { createRootRoute, Outlet, useNavigate, useRouterState } from '@tanstack/react-router'
import * as React from 'react'

export const Route = createRootRoute({
  component: RootComponent,
})

function RootComponent() {
  const [TopAppBar, setTopAppBar] = React.useState<React.ComponentType<
    Record<string, unknown>
  > | null>(null)
  const [MobileBottomNav, setMobileBottomNav] = React.useState<React.ComponentType<
    Record<string, unknown>
  > | null>(null)
  const [authed, setAuthed] = React.useState(false)
  const [devSessionLoading, setDevSessionLoading] = React.useState(false)
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const navigate = useNavigate()
  const isMarketingRoute = pathname === '/'
  const pathnameSegments = pathname.split('/').filter(Boolean)
  const isLookDetailRoute =
    pathnameSegments.length === 2 &&
    !['import', 'publish', 'scan', 'settings', 'tryon', 'remix'].includes(pathnameSegments[0] ?? '')
  const showMobileBottomNav = !isMarketingRoute && authed && MobileBottomNav !== null
  const mobileNavItems = [
    {
      href: '/looks',
      label: 'Looks',
      icon: 'looks',
      isActive: pathname === '/looks' || pathname.startsWith('/looks/') || isLookDetailRoute,
    },
    {
      href: '/kit',
      label: 'My Kit',
      icon: 'kit',
      isActive: pathname === '/kit' || pathname.startsWith('/kit/'),
    },
    {
      href: '/import',
      label: 'Create',
      icon: 'create',
      isActive:
        pathname === '/import' ||
        pathname.startsWith('/import/') ||
        pathname.startsWith('/capture') ||
        pathname.startsWith('/scan/') ||
        pathname.startsWith('/remix/') ||
        pathname.startsWith('/tryon/') ||
        pathname.startsWith('/publish/'),
    },
    {
      href: '/settings/privacy',
      label: 'Profile',
      icon: 'profile',
      isActive: pathname === '/settings/privacy' || pathname.startsWith('/settings/privacy/'),
    },
  ]

  React.useEffect(() => {
    import('@ootopia/ui').then((m) => {
      setTopAppBar(() => m.TopAppBar as never)
      setMobileBottomNav(() => m.MobileBottomNav as never)
    })
  }, [])

  React.useEffect(() => {
    fetch('/api/auth/session-state')
      .then(async (res) => {
        if (!res.ok) {
          setAuthed(false)
          return
        }
        const session = (await res.json()) as { authenticated?: boolean }
        setAuthed(session.authenticated === true)
      })
      .catch(() => setAuthed(false))
  }, [])

  async function handleDevSession() {
    setDevSessionLoading(true)
    try {
      const res = await fetch('/api/auth/dev-session', { method: 'POST' })
      if (res.ok) {
        setAuthed(true)
        navigate({ to: '/' })
      }
    } catch {
      // ignore — dev session not available
    } finally {
      setDevSessionLoading(false)
    }
  }

  return (
    <div className="min-h-dvh ootopia-shell">
      {!isMarketingRoute && TopAppBar ? (
        React.createElement(
          TopAppBar as never,
          {
            title: 'Ootopia',
            authenticated: authed,
            devSessionLoading,
            onDevSession: handleDevSession,
          } as never,
        )
      ) : !isMarketingRoute ? (
        <header className="ootopia-shell-header-fallback" />
      ) : null}
      {isMarketingRoute ? (
        <Outlet />
      ) : (
        <main
          className={`ootopia-shell-main mx-auto w-full max-w-[90rem] px-4 pb-16 sm:px-6 lg:px-10${
            showMobileBottomNav ? ' ootopia-shell-main--with-bottom-nav' : ''
          }`}
        >
          <Outlet />
        </main>
      )}
      {!isMarketingRoute && (
        <footer className="ootopia-footer ootopia-shell-footer px-4 py-10 sm:px-6 lg:px-10">
          <div className="mx-auto flex max-w-[82rem] flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
            <a href="/" className="flex items-center gap-2 text-sm font-black tracking-[-0.04em]">
              <img
                src="/brand/ootopia_logo_mark_nobg.svg?v=20260812"
                alt=""
                className="ootopia-footer-mark"
                aria-hidden="true"
              />
              <img
                src="/brand/ootopia_logo_text_nobg.svg?v=20260812"
                alt="ootopia"
                className="ootopia-footer-wordmark"
              />
            </a>
            <nav className="flex flex-wrap gap-x-5 gap-y-2 text-xs font-semibold text-foreground/55">
              <a href="/settings/privacy" className="hover:text-foreground">
                Privacy
              </a>
              <a href="/settings/privacy" className="hover:text-foreground">
                Delete my data
              </a>
              <a href="mailto:hello@ootopia.local" className="hover:text-foreground">
                Contact
              </a>
            </nav>
          </div>
        </footer>
      )}
      {showMobileBottomNav
        ? React.createElement(MobileBottomNav as never, { items: mobileNavItems } as never)
        : null}
    </div>
  )
}
